import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { Observable, tap, map } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import {
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableRowsSelectionChangeEvent,
  PrimengButtonDirective,
} from '@web/shared/ui';
import { Guid, isNotNil } from '@web/utils';
import {
  AdminCosmeticCollectionFilter,
  AdminCosmeticDataSourceService,
} from '@web/shared/module/cosmetic';
import {
  AdminBoxCollectionFilter,
  AdminBoxDataSourceService,
} from '@web/features/box';
import { AdminOrderShipmentComponentGroupType } from '../../enums/admin-order-shipment-component-group-type';
import { AdminOrderShipmentFormItem } from '../../models/admin-order-shipment-form-item';
import { AdminOrderShipmentSelectableItemFilter } from '../../models/admin-order-shipment-selectable-item-filter';
import { AdminOrderShipmentProductDataSourceService } from '../../services/admin-order-shipment-product-data-source.service';

@Component({
  selector: 'app-admin-order-shipment-item-select-modal',
  imports: [Button, Dialog, PrimengButtonDirective, TableComponent],
  templateUrl: './admin-order-shipment-item-select-modal.component.html',
  styleUrl: './admin-order-shipment-item-select-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOrderShipmentItemSelectModalComponent implements OnInit {
  private readonly builder = inject(
    TableBuilderFactory<
      AdminOrderShipmentFormItem,
      AdminOrderShipmentSelectableItemFilter,
      never,
      never
    >,
  ).create();
  private readonly cosmeticDataSource = inject(AdminCosmeticDataSourceService);
  private readonly boxDataSource = inject(AdminBoxDataSourceService);
  private readonly productDataSource = inject(
    AdminOrderShipmentProductDataSourceService,
  );

  public readonly excludedIds = input<Guid[]>([]);
  public readonly type = input.required<AdminOrderShipmentComponentGroupType>();
  public readonly visible = input<boolean>(false);
  public readonly cancelEvent = output<void>();
  public readonly submitEvent = output<AdminOrderShipmentFormItem[]>();

  protected config!: TableConfig<
    AdminOrderShipmentFormItem,
    AdminOrderShipmentSelectableItemFilter,
    never,
    never
  >;
  protected readonly loadedItems = new Map<Guid, AdminOrderShipmentFormItem>();

  private selectedIds: Guid[] = [];

  public ngOnInit(): void {
    this.buildTable();
  }

  protected handleVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelEvent.emit();
    }
  }

  protected handleSelectionChangeEvent(
    event: TableRowsSelectionChangeEvent<AdminOrderShipmentFormItem>,
  ): void {
    this.selectedIds = event.includedIds as Guid[];
  }

  protected handleSubmitClick(): void {
    this.submitEvent.emit(
      this.selectedIds.map((id) => this.loadedItems.get(id)).filter(isNotNil),
    );
  }

  protected subtitleLabel(): string {
    return this.type() === AdminOrderShipmentComponentGroupType.Box
      ? ''
      : 'Marka';
  }

  private buildTable(): void {
    const builder = this.builder
      .dataKey('id')
      .bodyRowSelectionDisabledFn((item) =>
        this.excludedIds().includes(item.id!),
      )
      .preserveSelectionOnFilter(true)
      .filters((builder) => {
        const filters = [builder.initText('name').build()];

        if (this.type() !== AdminOrderShipmentComponentGroupType.Box) {
          filters.push(builder.initText('sourceName').build());
        }

        return filters;
      })
      .columns((builder) => {
        const columns = [
          builder.initSelection().build(),
          builder
            .initText('name')
            .label('Nazwa')
            .sortable(true)
            .sortKey('name')
            .filterable(true)
            .filterKey('name')
            .build(),
        ];

        if (this.type() !== AdminOrderShipmentComponentGroupType.Box) {
          columns.push(
            builder
              .initText('sourceName')
              .label(this.subtitleLabel())
              .sortable(true)
              .sortKey('sourceName')
              .filterable(true)
              .filterKey('sourceName')
              .build(),
          );
        }

        return columns;
      });

    this.config = builder
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
        ): Observable<TableDataPayload<AdminOrderShipmentFormItem>> =>
          this.fetchCollection(lazyLoad).pipe(
            tap((payload) =>
              payload.data.forEach((item) => {
                if (isNotNil(item.id)) {
                  this.loadedItems.set(item.id, item);
                }
              }),
            ),
          ),
      })
      .build();
  }

  private fetchCollection(
    lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
  ): Observable<TableDataPayload<AdminOrderShipmentFormItem>> {
    switch (this.type()) {
      case AdminOrderShipmentComponentGroupType.Cosmetic:
        return this.cosmeticDataSource
          .fetchCollection(this.toCosmeticLazyLoad(lazyLoad))
          .pipe(
            map(
              (payload) =>
                new TableDataPayload(
                  payload.data.map(
                    (item) =>
                      new AdminOrderShipmentFormItem({
                        id: item.id,
                        name: item.name,
                        quantity: 1,
                        featuredFileAlt: item.featuredFileAlt,
                        featuredFileUrl: item.featuredFileUrl,
                        sourceName: item.cosmeticBrandName,
                      }),
                  ),
                  payload.size,
                  payload.page,
                  payload.total,
                ),
            ),
          );

      case AdminOrderShipmentComponentGroupType.Box:
        return this.boxDataSource
          .fetchCollection(this.toBoxLazyLoad(lazyLoad))
          .pipe(
            map(
              (payload) =>
                new TableDataPayload(
                  payload.data.map(
                    (item) =>
                      new AdminOrderShipmentFormItem({
                        id: item.id,
                        name: item.name,
                        quantity: 1,
                        featuredFileAlt: item.featuredFileAlt,
                        featuredFileUrl: item.featuredFileUrl,
                      }),
                  ),
                  payload.size,
                  payload.page,
                  payload.total,
                ),
            ),
          );

      case AdminOrderShipmentComponentGroupType.Product:
        return this.productDataSource.fetchCollection(lazyLoad);
    }
  }

  private toCosmeticLazyLoad(
    lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
  ): TableLazyLoad<AdminCosmeticCollectionFilter> {
    return new TableLazyLoad<AdminCosmeticCollectionFilter>(
      lazyLoad.first,
      lazyLoad.rows,
      lazyLoad.sort,
      {
        name: lazyLoad.filter?.name,
        cosmeticBrandName: lazyLoad.filter?.sourceName,
      },
    );
  }

  private toBoxLazyLoad(
    lazyLoad: TableLazyLoad<AdminOrderShipmentSelectableItemFilter>,
  ): TableLazyLoad<AdminBoxCollectionFilter> {
    return new TableLazyLoad<AdminBoxCollectionFilter>(
      lazyLoad.first,
      lazyLoad.rows,
      lazyLoad.sort,
      {
        name: lazyLoad.filter?.name,
      },
    );
  }
}
